import React, { useEffect, useState } from 'react';
import { Camera, CheckCircle2, Loader2, X } from 'lucide-react';
import { apiClient } from '../../utils/api';
import { useToast } from '../../components/Toast';
import {
  IdCardCapture,
  hasAnyIdCardSide,
  hasRequiredIdCardFront,
  type IdCardSides,
} from './IdCardCapture';
import { buildIdCardPhotosPayload } from './utils';

export interface SignedConfirmProps {
  signedAt: string;
  actId: string;
  isMinor: boolean;
  idCardClient: IdCardSides;
  idCardGuardian: IdCardSides;
  onIdCardClient: (v: IdCardSides) => void;
  onIdCardGuardian: (v: IdCardSides) => void;
  clientName: string;
  onReset: () => void;
  /** True when the signed contract on the server already has ID photos (e.g. after reload / back navigation). */
  serverHasPersistedIdCards?: boolean;
  onAfterIdCardsSaved?: () => void;
}

export const SignedConfirm: React.FC<SignedConfirmProps> = ({
  signedAt,
  actId,
  isMinor,
  idCardClient,
  idCardGuardian,
  onIdCardClient,
  onIdCardGuardian,
  clientName,
  onReset,
  serverHasPersistedIdCards = false,
  onAfterIdCardsSaved,
}) => {
  const { showToast, ToastComponent } = useToast();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [idCardsSaved, setIdCardsSaved] = useState(serverHasPersistedIdCards);
  /** True only after the practitioner edits local ID photos since last confirm/load. */
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setIdCardsSaved(serverHasPersistedIdCards);
    if (serverHasPersistedIdCards) setDirty(false);
  }, [serverHasPersistedIdCards]);

  const hasPhotos = hasAnyIdCardSide(idCardClient) || hasAnyIdCardSide(idCardGuardian);
  const minorReady =
    !isMinor ||
    (hasRequiredIdCardFront(idCardClient) && hasRequiredIdCardFront(idCardGuardian));
  /** Clickable only when there are unsaved local changes ready to persist. */
  const needsConfirmation = dirty && hasPhotos && minorReady;
  const showConfirmed = !dirty && (idCardsSaved || serverHasPersistedIdCards || hasPhotos);

  const markDirtyClient = (v: IdCardSides) => {
    setDirty(true);
    setIdCardsSaved(false);
    onIdCardClient(v);
  };

  const markDirtyGuardian = (v: IdCardSides) => {
    setDirty(true);
    setIdCardsSaved(false);
    onIdCardGuardian(v);
  };

  const handleReset = async () => {
    if (!window.confirm('Supprimer le contrat signé et recommencer la signature ?')) return;
    try {
      setResetting(true);
      const res = await apiClient(`/api/practitioner/acts/${actId}/contract`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erreur lors de la réinitialisation');
      onReset();
    } catch (err: any) {
      showToast(err?.message || 'Erreur lors de la réinitialisation', 'error', 4000);
      setResetting(false);
    }
  };

  const saveIdCards = async () => {
    if (!needsConfirmation || saving) return;
    try {
      setSaving(true);
      const photos = buildIdCardPhotosPayload(idCardClient, idCardGuardian);
      const res = await apiClient(`/api/practitioner/acts/${actId}/contract/id-cards`, {
        method: 'PATCH',
        body: JSON.stringify({ idCardPhotos: photos }),
      });
      if (!res.ok) throw new Error('HTTP');
      showToast("Pièces d'identité confirmées et enregistrées", 'success');
      setIdCardsSaved(true);
      setDirty(false);
      onAfterIdCardsSaved?.();
    } catch {
      showToast("Erreur lors de l'enregistrement", 'error', 4000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <ToastComponent />
      <div className="card border border-green-200 bg-green-50/30">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-bold text-sm">Contrat signé et archivé</p>
              <p className="text-xs text-green-600">{new Date(signedAt).toLocaleString('fr-FR')}</p>
            </div>
          </div>
          <button
            onClick={handleReset}
            disabled={resetting}
            title="Supprimer ce contrat et recommencer la signature"
            className="text-xs text-red-400 hover:text-red-600 border border-red-200 hover:border-red-400 rounded-lg px-2 py-1 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="card border border-gray-200 space-y-3">
        <h3 className="font-bold text-sm flex items-center gap-2">
          <Camera className="w-4 h-4" />
          Pièces d&apos;identité
          {!isMinor && <span className="text-xs font-normal text-gray-400">(optionnel)</span>}
        </h3>
        <p className="text-xs text-gray-500">
          Ajoutez le recto et le verso. Le bouton de confirmation s&apos;active uniquement après une
          modification non encore enregistrée.
        </p>
        <IdCardCapture
          label={`Carte d'identité — ${clientName}`}
          required={isMinor}
          sides={idCardClient}
          onSidesChange={markDirtyClient}
        />
        {isMinor && (
          <IdCardCapture
            label="Carte d'identité — Représentant légal"
            required
            sides={idCardGuardian}
            onSidesChange={markDirtyGuardian}
          />
        )}

        {dirty && hasPhotos && !minorReady && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Ajoutez le recto obligatoire (client et représentant légal) avant de confirmer.
          </p>
        )}

        {needsConfirmation ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 space-y-3">
            <p className="text-sm text-amber-900 font-medium">Confirmation requise</p>
            <p className="text-xs text-amber-800 leading-snug">
              Les pièces d&apos;identité modifiées ne sont pas encore attachées au contrat. Confirmez
              pour les enregistrer (archivage / traçabilité).
            </p>
            <button
              type="button"
              onClick={() => void saveIdCards()}
              disabled={!needsConfirmation || saving}
              aria-disabled={!needsConfirmation || saving}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…
                </>
              ) : (
                "Confirmer et enregistrer les pièces d'identité"
              )}
            </button>
          </div>
        ) : null}

        {showConfirmed && (
          <p className="text-xs text-green-600 flex items-center gap-1 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" /> Pièces d&apos;identité confirmées et enregistrées sur le contrat
          </p>
        )}
      </div>
    </div>
  );
};
